/**
 * 路径规划浮层面板
 *
 * 职责：
 * 1. 起终点来源切换（地图选点 / 输入坐标）
 * 2. 起点 / 终点坐标输入框（仅 input 模式渲染，可编辑 + 错误红框；pick 模式不渲染，坐标在信息区展示）
 * 3. 主操作按钮：开始规划 / 重新选点|清空坐标 / 清除路线
 * 4. 信息区：状态、起点、终点、里程 + 耗时
 *
 * 纯展示组件（React.memo），所有业务回调通过 props 注入；
 * 展开/收起为组件内局部 state（对齐 Legend 先例，不入 store）。
 * 面板根节点阻止 click/dblclick/mousedown/mouseup/wheel 冒泡，避免穿透到地图（PRD 16）。
 */
import React, { useCallback, useState } from 'react';
import { Button, Input, Radio } from 'antd';
import type { RadioChangeEvent } from 'antd/es/radio';
import type {
  PointSourceMode,
  RouteEndpoint,
  RouteStage,
} from '../store/routePlanStore';
import { formatDistance, formatDuration } from '../utils/routePlan';

export interface RoutePlanFloatPanelProps {
  pointSourceMode: PointSourceMode;
  routeStage: RouteStage;
  startInput: string;
  endInput: string;
  startInputError: string | null;
  endInputError: string | null;
  hasStartPoint: boolean;
  hasEndPoint: boolean;
  /** 里程，单位：米 */
  distance: number | null;
  /** 耗时，单位：秒 */
  duration: number | null;
  planError: string | null;
  onSwitchSourceMode: (mode: PointSourceMode) => void;
  onStartInputChange: (text: string) => void;
  onEndInputChange: (text: string) => void;
  onApplyInputPoint: (which: RouteEndpoint) => void;
  onStartPlan: () => void;
  onResetPoints: () => void;
  onClearRoute: () => void;
}

/** 状态区文案 */
const STAGE_TEXT: Record<RouteStage, string> = {
  idle: '请输入起点、终点坐标',
  pickingStart: '请点击地图选择起点',
  pickingEnd: '请点击地图选择终点',
  readyToPlan: '起终点已就绪，可开始规划',
  loading: '路径规划中…',
  success: '规划成功',
  fail: '路径规划失败',
};

/** 阻止事件穿透到地图（PRD 16） */
const stopPropagation = (e: React.SyntheticEvent) => {
  e.stopPropagation();
};

const RoutePlanFloatPanel = (props: RoutePlanFloatPanelProps) => {
  const {
    pointSourceMode,
    routeStage,
    startInput,
    endInput,
    startInputError,
    endInputError,
    hasStartPoint,
    hasEndPoint,
    distance,
    duration,
    planError,
    onSwitchSourceMode,
    onStartInputChange,
    onEndInputChange,
    onApplyInputPoint,
    onStartPlan,
    onResetPoints,
    onClearRoute,
  } = props;

  const [collapsed, setCollapsed] = useState(false);

  const isInputMode = pointSourceMode === 'input';
  const loading = routeStage === 'loading';

  // 按钮禁用为派生布尔：三组简单派生
  const startPlanDisabled =
    loading ||
    !hasStartPoint ||
    !hasEndPoint ||
    !!startInputError ||
    !!endInputError;
  const resetDisabled =
    loading ||
    (!hasStartPoint &&
      !hasEndPoint &&
      !startInput &&
      !endInput &&
      distance === null);
  const clearRouteDisabled = loading || distance === null;

  const statusText = routeStage === 'fail' && planError
    ? planError
    : STAGE_TEXT[routeStage];

  const handleSourceModeChange = useCallback(
    (e: RadioChangeEvent) => {
      onSwitchSourceMode(e.target.value as PointSourceMode);
    },
    [onSwitchSourceMode]
  );

  const handleStartInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onStartInputChange(e.target.value);
    },
    [onStartInputChange]
  );

  const handleEndInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onEndInputChange(e.target.value);
    },
    [onEndInputChange]
  );

  const handleApplyStart = useCallback(() => {
    onApplyInputPoint('start');
  }, [onApplyInputPoint]);

  const handleApplyEnd = useCallback(() => {
    onApplyInputPoint('end');
  }, [onApplyInputPoint]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  return (
    <div
      className="route-plan-panel"
      onClick={stopPropagation}
      onDoubleClick={stopPropagation}
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onPointerDown={stopPropagation}
      onPointerUp={stopPropagation}
      onTouchStart={stopPropagation}
      onTouchEnd={stopPropagation}
      onWheel={stopPropagation}
    >
      <div className="route-plan-panel-header">
        <span className="route-plan-panel-title">路径规划</span>
        <span className="route-plan-panel-collapse" onClick={toggleCollapsed}>
          {collapsed ? '展开' : '收起'}
        </span>
      </div>

      {!collapsed && (
        <div className="route-plan-panel-body">
          <Radio.Group
            className="route-plan-source-mode"
            value={pointSourceMode}
            onChange={handleSourceModeChange}
            disabled={loading}
          >
            <Radio value="pick">地图选点</Radio>
            <Radio value="input">输入坐标</Radio>
          </Radio.Group>

          {isInputMode && (
            <>
              <div className="route-plan-field">
                <span className="route-plan-field-label">起点</span>
                <Input
                  value={startInput}
                  status={startInputError ? 'error' : ''}
                  placeholder="lng,lat（GCJ02）"
                  onChange={handleStartInputChange}
                  onBlur={handleApplyStart}
                  onPressEnter={handleApplyStart}
                />
              </div>
              <div className="route-plan-field">
                <span className="route-plan-field-label">终点</span>
                <Input
                  value={endInput}
                  status={endInputError ? 'error' : ''}
                  placeholder="lng,lat（GCJ02）"
                  onChange={handleEndInputChange}
                  onBlur={handleApplyEnd}
                  onPressEnter={handleApplyEnd}
                />
              </div>
            </>
          )}

          <div className="route-plan-actions">
            <Button
              type="primary"
              disabled={startPlanDisabled}
              loading={loading}
              onClick={onStartPlan}
            >
              开始规划
            </Button>
            <Button disabled={resetDisabled} onClick={onResetPoints}>
              {isInputMode ? '清空坐标' : '重新选点'}
            </Button>
            <Button disabled={clearRouteDisabled} onClick={onClearRoute}>
              清除路线
            </Button>
          </div>

          <div className="route-plan-info">
            <div
              className={`route-plan-info-row route-plan-status${
                routeStage === 'fail' || startInputError || endInputError
                  ? ' route-plan-status--error'
                  : ''
              }`}
            >
              状态：{startInputError ?? endInputError ?? statusText}
            </div>
            <div className="route-plan-info-row">
              起点：{hasStartPoint ? startInput : '未选择'}
            </div>
            <div className="route-plan-info-row">
              终点：{hasEndPoint ? endInput : '未选择'}
            </div>
            {distance !== null && (
              <div className="route-plan-info-row">
                里程：{formatDistance(distance)} · {formatDuration(duration ?? 0)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(RoutePlanFloatPanel);
